import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Forum as ChatIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import toast from "react-hot-toast";
import PageContentLayout from "../components/layout/PageContentLayout";
import { useAuth } from "../contexts/AuthContext";
import apiService from "../services/apiService";

const FILTERS = [
  { key: "waiting", label: "Waiting" },
  { key: "mine", label: "Mine" },
  { key: "all", label: "All active" },
];

// Created By Cursor — CRM login stores admin on user or nested raw.data[0]
const resolveCrmAdmin = (user) => {
  const nested = Array.isArray(user?.raw?.data) ? user.raw.data[0] : null;
  const id = String(
    user?.admin_id ||
      user?.user_id ||
      user?.staff_id ||
      nested?.admin_id ||
      ""
  ).trim();
  const name = String(
    user?.admin_name ||
      user?.name ||
      user?.staff_name ||
      nested?.admin_name ||
      "Support"
  ).trim();
  return { id, name: name || "Support" };
};

const SupportChatInbox = () => {
  const { user } = useAuth();
  const { id: adminId, name: adminName } = useMemo(
    () => resolveCrmAdmin(user),
    [user]
  );

  const [filter, setFilter] = useState("waiting");
  const [inbox, setInbox] = useState([]);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [chat, setChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingThread, setLoadingThread] = useState(false);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [settings, setSettings] = useState({
    service_enabled: true,
    ai_enabled: false,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const threadEndRef = useRef(null);

  const loadSettings = useCallback(async () => {
    const result = await apiService.getSupportChatSettings();
    if (result.success && result.settings) {
      setSettings({
        service_enabled: result.settings.service_enabled !== false,
        ai_enabled: !!result.settings.ai_enabled,
      });
    }
  }, []);

  const saveSettings = async (next) => {
    setSavingSettings(true);
    const result = await apiService.updateSupportChatSettings({
      service_enabled: next.service_enabled,
      ai_enabled: next.service_enabled ? next.ai_enabled : false,
      admin_id: adminId,
    });
    setSavingSettings(false);
    if (!result.success) {
      toast.error(result.error || "Could not save settings");
      loadSettings();
      return;
    }
    setSettings({
      service_enabled: result.settings.service_enabled !== false,
      ai_enabled: !!result.settings.ai_enabled,
    });
    toast.success("Chat settings saved");
  };

  const loadInbox = useCallback(async () => {
    setLoadingInbox(true);
    const result = await apiService.getSupportChatInbox(filter, adminId, 30, 0);
    if (!result.success) {
      toast.error(result.error || result.message || "Failed to load inbox");
      setInbox([]);
    } else {
      const rows = Array.isArray(result.data) ? result.data : [];
      setInbox(rows);
    }
    setLoadingInbox(false);
  }, [filter, adminId]);

  const loadThread = useCallback(async (chatId) => {
    if (!chatId) return;
    setLoadingThread(true);
    const result = await apiService.getSupportChatMessages(chatId);
    if (!result.success) {
      toast.error(result.error || "Failed to load chat");
    } else {
      setChat(result.chat || null);
      setMessages(Array.isArray(result.messages) ? result.messages : []);
    }
    setLoadingThread(false);
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    loadInbox();
    const timer = setInterval(loadInbox, 10000);
    return () => clearInterval(timer);
  }, [loadInbox]);

  useEffect(() => {
    if (selectedId) loadThread(selectedId);
  }, [selectedId, loadThread]);

  useEffect(() => {
    if (!selectedId) return undefined;
    const timer = setInterval(() => loadThread(selectedId), 8000);
    return () => clearInterval(timer);
  }, [selectedId, loadThread]);

  useEffect(() => {
    threadEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const selectChat = (row) => {
    setSelectedId(row.chat_id);
  };

  const runAction = async (fn, successMsg) => {
    if (!selectedId || busy) return;
    if (!adminId) {
      toast.error("Admin id missing. Log in again.");
      return;
    }
    setBusy(true);
    const result = await fn();
    setBusy(false);
    if (!result.success) {
      toast.error(result.error || result.message || "Action failed");
      return;
    }
    if (successMsg) toast.success(successMsg);
    setChat(result.chat || null);
    setMessages(Array.isArray(result.messages) ? result.messages : []);
    loadInbox();
  };

  const onClaim = () =>
    runAction(() => apiService.claimSupportChat(selectedId, adminId, adminName), "Chat claimed");

  const onRelease = () =>
    runAction(() => apiService.releaseSupportChat(selectedId, adminId, adminName), "Returned to AI");

  const onClose = () =>
    runAction(() => apiService.closeSupportChatCrm(selectedId, adminId, adminName), "Chat closed");

  const onReply = async () => {
    const text = reply.trim();
    if (!text || !selectedId || busy) return;
    if (!adminId) {
      toast.error("Admin id missing. Log in again.");
      return;
    }
    setBusy(true);
    const result = await apiService.replySupportChat(selectedId, adminId, adminName, text);
    setBusy(false);
    if (!result.success) {
      toast.error(result.error || "Could not send reply");
      return;
    }
    setReply("");
    setChat(result.chat || null);
    setMessages(Array.isArray(result.messages) ? result.messages : []);
    loadInbox();
  };

  const modeChip = useMemo(() => {
    const mode = String(chat?.mode || "").toUpperCase();
    if (mode === "HUMAN") return { label: "Human", color: "warning" };
    if (mode === "CLOSED") return { label: "Closed", color: "default" };
    return { label: "AI", color: "info" };
  }, [chat]);

  return (
    <PageContentLayout
      title="Support Chat"
      subtitle="Monitor school chats. Human support is default; turn AI on only when needed."
      breadcrumbs={[
        { label: "Support Chat", icon: <ChatIcon sx={{ color: "#ffffff", fontSize: 18 }} /> },
      ]}
      backTo="/dashboard"
      actions={
        <Button
          size="small"
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={() => {
            loadInbox();
            loadSettings();
          }}
          sx={{ textTransform: "none" }}
        >
          Refresh
        </Button>
      }
    >
      <Card sx={{ mb: 2, p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
          Chat service controls
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!settings.service_enabled}
                disabled={savingSettings}
                onChange={(e) =>
                  saveSettings({
                    service_enabled: e.target.checked,
                    ai_enabled: e.target.checked ? settings.ai_enabled : false,
                  })
                }
              />
            }
            label={settings.service_enabled ? "Chat service ON" : "Chat service OFF"}
          />
          <FormControlLabel
            control={
              <Switch
                checked={!!settings.ai_enabled}
                disabled={savingSettings || !settings.service_enabled}
                onChange={(e) =>
                  saveSettings({
                    service_enabled: settings.service_enabled,
                    ai_enabled: e.target.checked,
                  })
                }
              />
            }
            label={settings.ai_enabled ? "AI mode ON" : "AI mode OFF (human default)"}
          />
          <Typography variant="caption" color="text.secondary">
            Default for new school chats: {settings.ai_enabled ? "AI" : "Human support"}
          </Typography>
        </Stack>
      </Card>

      <Card sx={{ mb: 2 }}>
        <Tabs value={filter} onChange={(e, v) => setFilter(v)} sx={{ px: 1 }}>
          {FILTERS.map((item) => (
            <Tab key={item.key} value={item.key} label={item.label} />
          ))}
        </Tabs>
      </Card>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "320px 1fr" },
          gap: 2,
          minHeight: 520,
        }}
      >
        <Card sx={{ p: 1, overflow: "auto", maxHeight: 640 }}>
          {loadingInbox ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : inbox.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              No chats in this filter.
            </Typography>
          ) : (
            inbox.map((row) => {
              const waiting =
                String(row.mode).toUpperCase() === "HUMAN" && !row.assigned_admin_id;
              const active = row.chat_id === selectedId;
              return (
                <Box
                  key={row.chat_id}
                  onClick={() => selectChat(row)}
                  sx={{
                    p: 1.5,
                    mb: 0.5,
                    borderRadius: 2,
                    cursor: "pointer",
                    bgcolor: active ? "action.selected" : waiting ? "#FFF8E1" : "transparent",
                    border: active ? "1px solid" : "1px solid transparent",
                    borderColor: active ? "primary.main" : "transparent",
                    "&:hover": { bgcolor: "action.hover" },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
                      {row.school_name || row.school_code}
                    </Typography>
                    {row.unread_crm > 0 ? (
                      <Chip size="small" color="error" label={row.unread_crm} />
                    ) : null}
                  </Stack>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {row.school_code} · {row.mode}
                    {row.assigned_admin_name ? ` · ${row.assigned_admin_name}` : ""}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ mt: 0.5 }}>
                    {row.preview || "—"}
                  </Typography>
                </Box>
              );
            })
          )}
        </Card>

        <Card sx={{ display: "flex", flexDirection: "column", minHeight: 520, maxHeight: 640 }}>
          {!selectedId ? (
            <Box sx={{ m: "auto", p: 3, textAlign: "center" }}>
              <Typography color="text.secondary">Select a school chat to monitor or reply.</Typography>
            </Box>
          ) : loadingThread && !chat ? (
            <Box sx={{ m: "auto" }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <Box sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="h6" sx={{ flex: 1, fontSize: 16 }}>
                    {chat?.school_name || chat?.school_code}
                  </Typography>
                  <Chip size="small" color={modeChip.color} label={modeChip.label} />
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {chat?.opened_by_name || chat?.opened_by} · {chat?.chat_id}
                  {chat?.assigned_admin_name ? ` · Assigned: ${chat.assigned_admin_name}` : ""}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap">
                  <Button size="small" variant="contained" disabled={busy} onClick={onClaim} sx={{ textTransform: "none" }}>
                    Claim
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={busy || !settings.ai_enabled}
                    onClick={onRelease}
                    sx={{ textTransform: "none" }}
                    title={!settings.ai_enabled ? "Turn on AI mode in settings first" : ""}
                  >
                    Resume AI
                  </Button>
                  <Button size="small" color="error" variant="outlined" disabled={busy} onClick={onClose} sx={{ textTransform: "none" }}>
                    Close
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ flex: 1, overflowY: "auto", p: 2, bgcolor: "grey.50" }}>
                {messages.map((message) => {
                  const role = message.sender_role;
                  const mine = role === "crm";
                  return (
                    <Box
                      key={message.message_id}
                      sx={{
                        display: "flex",
                        justifyContent: mine ? "flex-end" : "flex-start",
                        mb: 1,
                      }}
                    >
                      <Box
                        sx={{
                          maxWidth: "80%",
                          px: 1.5,
                          py: 1,
                          borderRadius: 2,
                          bgcolor: mine ? "primary.main" : role === "school" ? "#E3F2FD" : "#fff",
                          color: mine ? "#fff" : "text.primary",
                          border: mine ? "none" : "1px solid",
                          borderColor: "divider",
                          whiteSpace: "pre-wrap",
                          fontSize: 13,
                        }}
                      >
                        <Typography variant="caption" sx={{ opacity: 0.8, display: "block", mb: 0.25 }}>
                          {role === "school"
                            ? message.sender_name || "School"
                            : role === "ai"
                              ? "School AI"
                              : message.sender_name || "Support"}
                          {message.date_added ? ` · ${message.date_added}` : ""}
                        </Typography>
                        {message.message_text}
                      </Box>
                    </Box>
                  );
                })}
                <div ref={threadEndRef} />
              </Box>

              <Divider />
              <Box sx={{ p: 1.5, display: "flex", gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                  placeholder="Reply as support…"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  disabled={busy || String(chat?.status).toLowerCase() === "closed"}
                />
                <Button
                  variant="contained"
                  disabled={busy || !reply.trim()}
                  onClick={onReply}
                  sx={{ textTransform: "none", alignSelf: "flex-end" }}
                >
                  Send
                </Button>
              </Box>
            </>
          )}
        </Card>
      </Box>
    </PageContentLayout>
  );
};

export default SupportChatInbox;
